import { useRef } from "react";

import usePlayerEngine from "./hooks/usePlayerEngine";
import usePlayerControls from "./hooks/usePlayerControls";
import useAutoplayCountdown from "./hooks/useAutoplayCountdown";

import PlayerView from "./view";
import { LaunchDateBanner, PrivateVideoBanner } from "./view/components/Banners";

function isFutureDate(dateString) {
    if (!dateString) return false;
    return new Date(dateString) > new Date();
}

/**
 * Player — controller for the custom content video player.
 *
 * Applies the availability rules (scheduled launch, premium gating), then
 * assembles the media engine, control state and autoplay flow for the view.
 *
 * @param {object}   info        - the lesson/content payload
 * @param {'horizontal'|'vertical'} mode - landscape overlays controls on the
 *                                  video; portrait moves them to a side rail
 * @param {object}   prevContent - previous playlist item, if any
 * @param {object}   nextContent - next playlist item, if any
 * @param {function} onNavigate  - navigate to another playlist content
 */
export default function Player({ info, mode = "horizontal", prevContent, nextContent, onNavigate }) {
    const videoRef = useRef(null);
    const rootRef = useRef(null);

    const futureDate = info?.timestamps?.launch || info?.availability?.available_at;
    const isFuture = isFutureDate(futureDate);
    const isAvailable = info?.availability?.available;
    const hasPermissions = ["admin", "owner", "creator"].includes(info?.role);

    const autoplay = useAutoplayCountdown({
        videoRef,
        contentId: info.id,
        nextContent,
        onNavigate,
    });

    const { watched, toggleWatched, needsPlayGesture } = usePlayerEngine({
        info,
        videoRef,
        onEnded: autoplay.trigger,
    });

    const controls = usePlayerControls({ videoRef, rootRef });

    // Scheduled content: only admins/owners/creators may preview it.
    if (isFuture && !hasPermissions) {
        return (
            <div className={`player ${mode} banner-only`}>
                <div className="player-stage">
                    <LaunchDateBanner content={info} />
                </div>
            </div>
        );
    }

    // Premium content the current user has no access to.
    if (!isFuture && !isAvailable && !hasPermissions && info?.id) {
        return (
            <div className={`player ${mode} banner-only`}>
                <div className="player-stage">
                    <PrivateVideoBanner content={info} community={info?.community} />
                </div>
            </div>
        );
    }

    return (
        <PlayerView
            info={info}
            mode={mode}
            videoRef={videoRef}
            rootRef={rootRef}
            controls={controls}
            watched={watched}
            onToggleWatched={toggleWatched}
            prevContent={prevContent}
            nextContent={nextContent}
            onNavigate={onNavigate}
            autoplay={autoplay}
            isAdminPreview={hasPermissions && isFuture}
            needsPlayGesture={needsPlayGesture}
        />
    );
}
